import {Stack} from "@mui/material";
import SvgIcon from "@mui/material/SvgIcon";
import FullscreenExitIcon from "@mui/icons-material/FullscreenExit";
import FullscreenIcon from "@mui/icons-material/Fullscreen";
import {useEffect, useState} from "react";
import {BrowserMenuButton} from "./BrowserMenuButton";
import {PANEL_SLIDE_TRANSITION} from "./useNavLayout";
import "../../../animations/menuToggle.css";

/** Inset from the edges of the panorama, so nothing floating over it sits on the very corner. */
export const CONTROLS_INSET_PX = 24;

function fullscreenElement() {
    const doc = document as any;
    return document.fullscreenElement ?? doc.webkitFullscreenElement ?? doc.msFullscreenElement ?? null;
}

/** iOS Safari implements fullscreen for video elements only, where the button would silently do nothing. */
function isFullscreenSupported() {
    const doc = document as any;
    return Boolean(document.fullscreenEnabled ?? doc.webkitFullscreenEnabled ?? doc.msFullscreenEnabled);
}

function openFullscreen() {
    const elem = document.documentElement as any;

    if (elem.requestFullscreen) {
        elem.requestFullscreen();
    } else if (elem.webkitRequestFullscreen) { /* Safari */
        elem.webkitRequestFullscreen();
    } else if (elem.msRequestFullscreen) { /* IE11 */
        elem.msRequestFullscreen();
    }
}

function closeFullscreen() {
    const doc = document as any;

    if (document.exitFullscreen) {
        document.exitFullscreen();
    } else if (doc.webkitExitFullscreen) { /* Safari */
        doc.webkitExitFullscreen();
    } else if (doc.msExitFullscreen) { /* IE11 */
        doc.msExitFullscreen();
    }
}

const VRModeIcon = () => <SvgIcon fontSize={"large"} fill={"currentColor"}>
    <svg version="1.1" id="Icons" xmlns="http://www.w3.org/2000/svg"
         xmlnsXlink="http://www.w3.org/1999/xlink" viewBox="1 0 30 30"
         xmlSpace="preserve">
        <mask id="cutout">
            <rect width="100%" height="100%" fill="white"/>

            <circle cx="9" cy="16" r="3" fill="black"/>
            <circle cx="23" cy="16" r="3" fill="black"/>
        </mask>

        <g id="SVGRepo_iconCarrier">
            <path mask="url(#cutout)"
                  d="M3,11v10c0,1.1,0.9,2,2,2h6.2c0.5,0,1-0.2,1.4-0.6l2-2c0.8-0.8,2-0.8,2.8,0l2,2c0.4,0.4,0.9,0.6,1.4,0.6H27 c1.1,0,2-0.9,2-2V11c0-1.1-0.9-2-2-2H5C3.9,9,3,9.9,3,11z"></path>
            <circle cx="9" cy="16" r="3" fill={"none"}></circle>
            <circle cx="23" cy="16" r="3" fill={"none"}></circle>
        </g>
    </svg>
</SvgIcon>;

export interface ViewControlsProps {
    /** Keeps the controls clear of the navigation panel, which is anchored to the right edge. */
    rightOffsetPx: number;
}

/** The controls that act on the view rather than on navigation: fullscreen and entering VR. */
export function ViewControls(props: ViewControlsProps) {
    const {rightOffsetPx} = props;

    const [isFullScreen, setIsFullScreen] = useState<boolean>(Boolean(fullscreenElement()));

    // @ts-ignore Is VR supported on the environment
    const canEnterVR = AFRAME.utils.device.checkVRSupport();
    const canFullscreen = isFullscreenSupported();

    // Fullscreen can also be left with Escape or the browser's own chrome, so follow the document
    // rather than only our own clicks.
    useEffect(() => {
        const syncFullScreenState = () => setIsFullScreen(Boolean(fullscreenElement()));

        document.addEventListener("fullscreenchange", syncFullScreenState);
        document.addEventListener("webkitfullscreenchange", syncFullScreenState);

        return () => {
            document.removeEventListener("fullscreenchange", syncFullScreenState);
            document.removeEventListener("webkitfullscreenchange", syncFullScreenState);
        };
    }, []);

    if (!canFullscreen && !canEnterVR) {
        return <></>;
    }

    return <Stack spacing={2} direction={"column"} alignItems={"center"}
                  sx={{
                      pointerEvents: "auto",
                      position: "absolute",
                      top: CONTROLS_INSET_PX,
                      right: rightOffsetPx + CONTROLS_INSET_PX,
                      // Rides along with the navigation panel opening and closing.
                      transition: `right ${PANEL_SLIDE_TRANSITION}`
                  }}>

        {canFullscreen && <BrowserMenuButton tooltip={isFullScreen ? "Exit fullscreen" : "Open fullscreen"}
                                             ariaLabel={isFullScreen ? "Exit fullscreen" : "Open fullscreen"}
                                             className={"hidden-menu-item"}
                                             onClick={() => isFullScreen ? closeFullscreen() : openFullscreen()}
                                             icon={isFullScreen ? <FullscreenExitIcon/> : <FullscreenIcon/>}/>}

        {/* A-Frame's xr-mode-ui binds to this id (see PanoramaScene), so it must keep it and stay in the DOM. */}
        {canEnterVR && <BrowserMenuButton id={"enterVRButton"}
                                          ariaLabel={"Enter VR"}
                                          className={"hidden-menu-item"}
                                          icon={<VRModeIcon/>}/>}
    </Stack>;
}
